import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import React, { useEffect, useRef, useState } from 'react';

import useTravelUser from '@flight-workspace/common-client/src/hooks/useTravelUser';
import {
  useKoreanUnitSeparators,
  withPriceSeperator,
} from '@flight-workspace/common-client/src/util/number';
import AgentInfo from 'common/agentInfo';
import { getCsType } from 'common/agentInfo/util';
import OtaLogo from 'common/otaLogo';
import { IPromotion } from 'common/promotion';
import { useToast } from 'common/searchBox/Toast';
import { isTravelClub } from 'common/travelLabel';
import { IBookingPartnersHashMap } from 'common/travelLabel/types';
import { IKeyStringValue } from 'common/types';
import { getNeedNotice, getNpayLabelInfo } from 'common/utils';
import config from 'config/index';
import useFlightsCards from 'hooks/useFlightsCards';
import Travelclub from 'list/common/travelclub';
import {
  ConfirmTypeCode,
  IFareDetail,
  IScheduleDetail,
} from 'list/international/types';
import { getPriceOfPerson, hasAdultNaverFare } from 'list/international/utils';
import { PriceTransparency } from 'types/apollo';
import DescriptionMy from './descriptionMy';
import styles from './index.module.scss';
import Notice from './notice';
import Promotion from './npayPromotion';
import PassengerPriceItem from './passengerPriceItem';
import GET_PRICE_TRANSPARENCY from './priceTransparency.gql';
import getPromotions from './promotion';
import Tags from './tags';
import { IOta, IPassengerCount, OtaType } from './types';
import { areaCode, getTotalPrice } from './utils';

const cx = classNames.bind(styles);

const MAX_OTA_INDEX = 11;

interface IProps {
  bookingPartnersHashMap: IBookingPartnersHashMap;
  checkOne: boolean;
  departureDate: string;
  fare: IFareDetail;
  idx: number;
  korFareTypes: IKeyStringValue;
  ota: IOta;
  passengerCount: IPassengerCount;
  promotions: IPromotion[];
  selectedScheduleDetails: IScheduleDetail[][];
  onClickBooking: (ota: IOta, index: number) => () => void;
  setMinFare: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export default function BookingOption({
  bookingPartnersHashMap,
  checkOne,
  departureDate,
  fare,
  idx,
  korFareTypes,
  ota,
  passengerCount,
  passengerCount: { adultCnt, childCnt, infantCnt },
  promotions,
  selectedScheduleDetails,
  onClickBooking,
  setMinFare,
}: IProps) {
  const [isNoticeOn, setIsNoticeOn] = useState(false);
  const bookingOptionRef = useRef<HTMLDivElement>(null);
  const {
    login,
    userTravelClub: { grade, getSavingRate },
  } = useTravelUser({
    nAuthWithUrlParam: config.nAuthWithUrlParam,
  });
  const [selectedDetailIndex, setSelectedDetailIndex] = useState(-1);
  const {
    active,
    csInfo,
    expName: otaName,
    fare: totalFare,
    isCsCenter,
    isCsCenterEmail,
    otaId,
    otaType,
    refundPolicy,
    supportNaverLogin,
  } = ota;

  const isGlobal =
    otaType === OtaType.GLOBAL_AIRLINE || otaType === OtaType.GLOBAL_OTA;
  const csType = getCsType(isCsCenter, isCsCenterEmail);
  const {
    FareType,
    Adult,
    Child,
    Infant,
    hideMemberFare, // 비로그인시 true(비로그인상태에서 멤버쉽 요금은 가려진 상태)
    isMemberFare, // 로그인시 true
    priceTransparencyUrl: _priceTransparencyUrl,
    isHoneymoonBenefit,
    ConfirmType,
  } = fare;

  const priceTransparencyUrl = _priceTransparencyUrl?.['#cdata-section'];
  const {
    data: { priceTransparency } = {},
    loading: priceTransparencyLoading,
  } = useQuery<{ priceTransparency: PriceTransparency }>(
    GET_PRICE_TRANSPARENCY,
    {
      variables: { otaId, priceTransparencyUrl },
      skip: !priceTransparencyUrl,
    },
  );

  const promotionInfo = getPromotions(
    promotions,
    selectedScheduleDetails,
    fare,
    passengerCount,
    totalFare,
  );
  const isDiscountPromotion = hasAdultNaverFare(fare);
  const isNPayPromotion =
    isDiscountPromotion && !!promotionInfo?.promotions?.[0];
  const isExposableMemberFare = isMemberFare || hideMemberFare;
  const otaIndex = idx < MAX_OTA_INDEX ? idx + 1 : MAX_OTA_INDEX;
  const originalTotalFare = getTotalPrice(fare, adultCnt, childCnt, infantCnt);
  const discountTotalFare = getTotalPrice(
    fare,
    adultCnt,
    childCnt,
    infantCnt,
    true,
  );
  const travelClubSavingRate =
    isTravelClub({ grade, otaId, bookingPartnersHashMap }) &&
    getSavingRate('flight', departureDate, otaId, originalTotalFare);
  const isFreeSameDayRefundable =
    refundPolicy?.isFreeSameDayRefundable && fare?.isFreeSameDayRefundable;
  const minFarePerPerson = getPriceOfPerson(Adult, true);

  const needNotice = getNeedNotice(otaId, otaType);
  const onReservationClick = needNotice
    ? () => setIsNoticeOn(true)
    : onClickBooking(ota, otaIndex);

  useEffect(() => {
    if (!idx && minFarePerPerson) setMinFare(minFarePerPerson);
  }, [idx, minFarePerPerson]);

  const [showNpayLabelPopup, setShowNpayLabelPopup] = useToast();
  const flightCards = useFlightsCards();
  const nPayLabelInfo = getNpayLabelInfo(FareType, flightCards);

  const handleScroll = (buttonRef: React.RefObject<HTMLDivElement>) => {
    if (selectedDetailIndex !== idx) {
      buttonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <div
      className={cx('BookingOption', 'has_tags')}
      key={`ota_${idx}`}
      ref={bookingOptionRef}
      aria-expanded={selectedDetailIndex === idx}
    >
      <div className={cx('summary_wrapper')}>
        <div className={cx('summary')}>
          {otaId && (
            <div className={cx('logo')}>
              <OtaLogo id={otaId} name={otaName} />
            </div>
          )}
          <div
            className={cx('offer')}
            data-withmembership={isExposableMemberFare}
            itemScope
          >
            <span className={cx('ota')} itemProp="seller">
              {csInfo?.active || isFreeSameDayRefundable ? (
                <AgentInfo
                  clickCodePrefix="idt"
                  otaIndex={otaIndex}
                  otaInfo={ota}
                  isFreeSameDayRefundable={isFreeSameDayRefundable}
                />
              ) : (
                <i className={cx('name')}>{otaName}</i>
              )}
              <span className={cx('labels')}>
                {(otaType === OtaType.KR_AIRLINE ||
                  otaType === OtaType.GLOBAL_AIRLINE) && (
                  <i className={cx('agent_airline')}>항공사</i>
                )}
                {otaType === OtaType.GLOBAL_OTA && (
                  <i className={cx('agent_foreign')}>해외여행사</i>
                )}
              </span>
              {!checkOne && (
                <span className={cx('faretypes')} role="row">
                  {korFareTypes[FareType]}
                </span>
              )}
            </span>
            <div className={cx('offer_type')}>
              <div className={cx('pc_tag_wrapper')}>
                <Tags
                  fare={fare}
                  isExpanded={selectedDetailIndex === idx}
                  isFreeSameDayRefundable={isFreeSameDayRefundable}
                  originalTotalFare={originalTotalFare}
                  priceTransparency={priceTransparency}
                  priceTransparencyLoading={priceTransparencyLoading}
                />
              </div>
            </div>
            <div
              className={cx('fare')}
              data-withlables={
                isFreeSameDayRefundable && fare?.isHoneymoonBenefit
              }
            >
              {isExposableMemberFare && (
                <div className={cx('condition')} itemProp="itemCondition">
                  <span className={cx('travelclub')}>
                    <i className={cx('service')} title="트래블클럽" />
                    <span className={cx('txt')}>할인</span>
                  </span>
                </div>
              )}
              {isDiscountPromotion ? (
                <>
                  <del className={cx('before')} itemProp="offers">
                    <b className={cx('num')}>
                      {withPriceSeperator(originalTotalFare)}
                    </b>
                    <span className={cx('unit')}>원</span>
                  </del>
                  <div className={cx('condition')} itemProp="itemCondition">
                    <span className={cx('directpromotion')}>
                      {useKoreanUnitSeparators(
                        originalTotalFare - discountTotalFare,
                      )}
                      원 할인
                    </span>
                  </div>
                  <b className={cx('promoted')} itemProp="offers">
                    <b className={cx('num')}>
                      {withPriceSeperator(discountTotalFare)}
                    </b>
                    <span className={cx('unit')}>원</span>
                  </b>
                </>
              ) : (
                <>
                  {/* 적립프로모션은 멤버쉽용 로그인 유도가 없음 */}
                  {!isNPayPromotion &&
                  hideMemberFare &&
                  isExposableMemberFare ? (
                    <a className={cx('login')} href="#">
                      <span className={cx('txt')}>특가확인</span>
                      <i className={cx('nlogin')} title="NAVER 로그인" />
                    </a>
                  ) : (
                    <strong className={cx('price')} itemProp="offers">
                      <b className={cx('num')}>
                        {withPriceSeperator(originalTotalFare)}
                      </b>
                      <span className={cx('unit')}>원</span>
                    </strong>
                  )}
                </>
              )}
              {isNPayPromotion && (
                <div
                  className={cx('condition')}
                  itemProp="itemCondition"
                  role="row"
                >
                  <Promotion promotion={promotionInfo.promotions[0]} />
                </div>
              )}
              {!!nPayLabelInfo && (
                <div
                  className={cx('condition')}
                  itemProp="itemCondition"
                  role="row"
                >
                  <button
                    className={cx('label_npay')}
                    onClick={() =>
                      setShowNpayLabelPopup(showNpayLabelPopup ? '' : 'show')
                    }
                    onMouseEnter={() =>
                      setShowNpayLabelPopup(showNpayLabelPopup ? '' : 'show')
                    }
                    aria-haspopup="true"
                    aria-expanded={!!showNpayLabelPopup}
                  >
                    <i className={cx('label')} aria-label="네이버페이" />
                    {`${nPayLabelInfo.nPayPromotionAmount}% 적립`}
                  </button>
                  <div className={cx('popup')}>
                    {nPayLabelInfo.nPayLabelPopupText}
                  </div>
                </div>
              )}
              {!!travelClubSavingRate && (
                <div
                  className={cx('condition')}
                  itemProp="itemCondition"
                  role="row"
                >
                  <Travelclub
                    savingRate={travelClubSavingRate}
                    isMemberFare={!!isMemberFare}
                  />
                </div>
              )}
            </div>
          </div>
          <span className={cx('toggle')} />
        </div>
        <div
          className={cx('mobile_tag_wrapper', {
            has_tags:
              priceTransparencyLoading ||
              (priceTransparency?.detail?.length ?? 0) > 0 ||
              isFreeSameDayRefundable ||
              isHoneymoonBenefit ||
              ConfirmType !== ConfirmTypeCode.CONFIRMED,
          })}
        >
          <Tags
            fare={fare}
            isExpanded={selectedDetailIndex === idx}
            isFreeSameDayRefundable={isFreeSameDayRefundable}
            originalTotalFare={originalTotalFare}
            priceTransparency={priceTransparency}
            priceTransparencyLoading={priceTransparencyLoading}
          />
        </div>
        <button
          className={cx('button_expand')}
          type="button"
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.otaprice${otaIndex}`}
          onClick={e => {
            handleScroll(bookingOptionRef);
            if (hideMemberFare && !(isDiscountPromotion || isNPayPromotion)) {
              // 비로그인, 멤버쉽 요금 가려짐, 로그인으로 이동
              login(window.location.href);
            } else {
              setSelectedDetailIndex(prev => (prev === idx ? -1 : idx));
            }
          }}
        />
        <a
          className={cx('detail_anchor')}
          href="#none"
          rel="noreferrer"
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.otabook${otaIndex}`}
          onClick={onReservationClick}
          title={`${otaName}에서 예약`}
        />
      </div>
      {selectedDetailIndex === idx && (
        <div className={cx('detail')}>
          <PassengerPriceItem
            count={adultCnt}
            fareItem={Adult}
            isDiscountPromotion={isDiscountPromotion}
            passengerKor="성인"
          />
          {childCnt > 0 && (
            <PassengerPriceItem
              count={childCnt}
              fareItem={Child}
              isDiscountPromotion={isDiscountPromotion}
              passengerKor="소아"
            />
          )}
          {infantCnt > 0 && (
            <PassengerPriceItem
              count={infantCnt}
              fareItem={Infant}
              isDiscountPromotion={isDiscountPromotion}
              passengerKor="유아"
            />
          )}
          <div className={cx('total')}>
            <span className={cx('name')}>총 예상요금</span>
            <strong className={cx('price')}>
              <b className={cx('num')}>{withPriceSeperator(totalFare)}</b>
              <span className={cx('unit')}>원</span>
            </strong>
          </div>
          {isNPayPromotion &&
            promotionInfo.promotions.map((promotion, index) => (
              <div key={index} className={cx('total_promotion')}>
                <Promotion promotion={promotion} />
              </div>
            ))}
          <p className={cx('info')}>유류할증료/제세공과금 등 포함</p>
          <div className={cx('type')}></div>
          <a
            href="#none"
            rel="noreferrer"
            className={cx('reserve')}
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.otabook${otaIndex}`}
            onClick={onReservationClick}
          >
            <span className={cx('icons')}>
              {otaId === 'CTR013' || FareType.includes('B74') ? (
                <span className={cx('label_npay')}>
                  <i className={cx('label')} aria-label="네이버페이" />
                </span>
              ) : supportNaverLogin ? (
                <i className={cx('login')} title="네이버로그인" />
              ) : null}
            </span>
            <b className={cx('txt')}>{otaName} 예약</b>
          </a>
          {isNPayPromotion &&
            promotionInfo.descriptions.map((description, index) => (
              <div key={index} className={cx('description')}>
                <p className={cx('txt')}>{description}</p>
              </div>
            ))}
          {isFreeSameDayRefundable && (
            <div className={cx('description')}>
              <p className={cx('txt')}>
                당일 무료 취소 항공권 예약 시, 결제 당일 23:50 전까지 전액
                환불이 지원됩니다.
              </p>
            </div>
          )}
          {!isGlobal && (
            <>
              <DescriptionMy active={active?.mypage} otaName={otaName} />
              <div className={cx('description')}>
                <p className={cx('txt')}>
                  요금규정(수하물, 환불, 변경, 마일리지 등 관련)과 예약 가능
                  여부 및 요금(유류할증료, 제세공과금 등 포함) 정보는 여행사에서
                  제공 받은 참고용 자료입니다. 여행사의 데이터 전송 여부에 따라
                  요금규정이 제공되지 않을 수 있고, 네이버 검색결과에 노출되는
                  정보와 여행사 홈페이지 정보에 차이가 있을 수 있습니다. 자세한
                  내용은 항공권 예약 시 여행사 홈페이지에서 반드시 확인하시기
                  바랍니다.
                </p>
              </div>
              <div className={cx('description')}>
                <p className={cx('txt')}>
                  네이버는 통신판매의 당사자가 아니며, 항공권 검색 결과 및 구매
                  및 환불 등에 대한 별도의 책임을 지지 않습니다. 항공권 예약,
                  결제, 발권, 취소 등에 대한 사항은 해당 여행사로 문의하시기
                  바랍니다.
                </p>
              </div>
            </>
          )}
          {isGlobal && (
            <>
              <DescriptionMy active={active?.mypage} otaName={otaName} />
              <div className={cx('description')}>
                <p className={cx('txt')}>
                  해외업체에서 제공하는 일부 항공권은 결제 후 환불 및 변경이
                  불가할 수 있습니다. 예약 전 업체별 요금규정을 반드시
                  확인하세요.
                </p>
              </div>
              <div className={cx('description')}>
                <p className={cx('txt')}>
                  해외지역 출발 항공권은 제휴사 페이지의 항공권 가격이 출발지의
                  통화로 표시될 수 있으며, 환율에 따라 네이버에서 확인한 원화
                  금액과 차이가 발생할 수 있습니다.
                </p>
              </div>
              <div className={cx('description')}>
                <p className={cx('txt')}>
                  해외 결제 시 결제수단에 따라 추가 수수료가 부가될 수 있습니다.
                </p>
              </div>
              <div className={cx('description')}>
                <p className={cx('txt')}>
                  {otaName}은/는 해외업체로 한국어 고객센터
                  {csType ? `(${csType})` : ''}를{' '}
                  {csType ? `운영합니다.` : `운영하지 않습니다.`}{' '}
                  {(csInfo?.phone1 || csInfo?.email) &&
                    `(${[
                      csInfo?.phone1 && `전화: ${csInfo?.phone1}`,
                      csInfo?.email && `이메일: ${csInfo?.email}`,
                    ]
                      .filter(Boolean)
                      .join(' / ')})`}
                </p>
              </div>
            </>
          )}
        </div>
      )}
      {isNoticeOn && (
        <Notice
          areaCode="idt"
          onClickBooking={onClickBooking(ota, otaIndex)}
          onClose={() => setIsNoticeOn(false)}
        />
      )}
    </div>
  );
}
